<template>
  <div class="shipment-document-form">
    <v-row>
      <v-col cols="12" md="6">
        <v-card outlined class="cmr-party-card pa-4">
          <div class="cmr-party-card__title">{{ $t("STOCK.CMR.GROUP_SENDER") }}</div>
          <v-text-field
            v-model="local.sender.company_name"
            :label="$t('STOCK.CMR.COMPANY_NAME')"
            :disabled="disabled"
          ></v-text-field>
          <v-textarea
            v-model="local.sender.address"
            rows="2"
            auto-grow
            :label="$t('STOCK.CMR.ADDRESS')"
            :disabled="disabled"
          ></v-textarea>
          <v-text-field
            v-model="local.sender.country"
            :label="$t('STOCK.CMR.COUNTRY')"
            :disabled="disabled"
          ></v-text-field>
          <v-text-field
            v-model="local.sender.vat_number"
            :label="$t('STOCK.CMR.VAT_NUMBER')"
            :disabled="disabled"
          ></v-text-field>
          <v-textarea
            v-model="local.place_and_date_of_taking_over"
            rows="3"
            auto-grow
            :label="$t('STOCK.CMR.PLACE_AND_DATE_OF_TAKING_OVER')"
            :disabled="disabled"
          ></v-textarea>
        </v-card>
      </v-col>
      <v-col cols="12" md="6">
        <v-card outlined class="cmr-party-card pa-4">
          <div class="cmr-party-card__title">{{ $t("STOCK.CMR.GROUP_CONSIGNEE") }}</div>
          <v-text-field
            v-model="local.consignee.company_name"
            :label="$t('STOCK.CMR.COMPANY_NAME')"
            :disabled="disabled"
          ></v-text-field>
          <v-textarea
            v-model="local.consignee.address"
            rows="2"
            auto-grow
            :label="$t('STOCK.CMR.ADDRESS')"
            :disabled="disabled"
          ></v-textarea>
          <v-text-field
            v-model="local.consignee.country"
            :label="$t('STOCK.CMR.COUNTRY')"
            :disabled="disabled"
          ></v-text-field>
          <v-text-field
            v-model="local.consignee.vat_number"
            :label="$t('STOCK.CMR.VAT_NUMBER')"
            :disabled="disabled"
          ></v-text-field>
          <v-textarea
            v-model="local.place_of_delivery"
            rows="3"
            auto-grow
            :label="$t('STOCK.CMR.PLACE_OF_DELIVERY')"
            :disabled="disabled"
          ></v-textarea>
        </v-card>
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12" md="6">
        <v-subheader class="px-0">{{ $t("STOCK.CMR.GROUP_BOO_1") }}</v-subheader>
        <v-text-field
          v-model="local.boo_1.company_name"
          :label="$t('STOCK.CMR.COMPANY_NAME')"
          :disabled="disabled"
        ></v-text-field>
        <v-text-field
          v-model="local.boo_1.vat_number"
          :label="$t('STOCK.CMR.VAT_NUMBER')"
          :disabled="disabled"
        ></v-text-field>
      </v-col>
      <v-col cols="12" md="6">
        <v-subheader class="px-0">{{ $t("STOCK.CMR.GROUP_BOO_2") }}</v-subheader>
        <v-text-field
          v-model="local.boo_2.company_name"
          :label="$t('STOCK.CMR.COMPANY_NAME')"
          :disabled="disabled"
        ></v-text-field>
        <v-text-field
          v-model="local.boo_2.vat_number"
          :label="$t('STOCK.CMR.VAT_NUMBER')"
          :disabled="disabled"
        ></v-text-field>
      </v-col>
    </v-row>

    <v-subheader class="px-0">{{ $t("STOCK.CMR.GROUP_GOODS") }}</v-subheader>
    <v-simple-table dense class="mb-3">
      <thead>
        <tr>
          <th>{{ $t("STOCK.CMR.GOODS_NAME") }}</th>
          <th style="width: 140px">{{ $t("STOCK.CMR.HS_CODE") }}</th>
          <th style="width: 150px">{{ $t("STOCK.CMR.GROSS_WEIGHT") }}</th>
          <th style="width: 150px">{{ $t("STOCK.CMR.NET_WEIGHT") }}</th>
          <th style="width: 48px"></th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="(line, index) in local.goods_lines" :key="line._key">
          <td>
            <v-text-field v-model="line.name" dense hide-details :disabled="disabled"></v-text-field>
          </td>
          <td>
            <v-text-field v-model="line.hs_code" dense hide-details :disabled="disabled"></v-text-field>
          </td>
          <td>
            <v-text-field
              v-model="line.gross_weight"
              type="number"
              step="0.001"
              dense
              hide-details
              suffix="kg"
              :disabled="disabled"
            ></v-text-field>
          </td>
          <td>
            <v-text-field
              v-model="line.net_weight"
              type="number"
              step="0.001"
              dense
              hide-details
              suffix="kg"
              :disabled="disabled"
            ></v-text-field>
          </td>
          <td>
            <v-btn icon small :disabled="disabled" @click="removeGoodsLine(index)">
              <v-icon small>mdi-delete</v-icon>
            </v-btn>
          </td>
        </tr>
      </tbody>
    </v-simple-table>
    <v-btn
      small
      outlined
      color="primary"
      class="mb-4"
      :disabled="disabled || local.goods_lines.length >= maxGoodsLines"
      @click="addGoodsLine"
    >
      <v-icon left small>mdi-plus</v-icon>
      {{ $t("STOCK.CMR.ADD_GOODS_LINE") }}
    </v-btn>

    <v-row>
      <v-col cols="12" md="6">
        <v-text-field
          v-model="local.established_in"
          :label="$t('STOCK.CMR.ESTABLISHED_IN')"
          :disabled="disabled"
        ></v-text-field>
      </v-col>
      <v-col cols="12" md="3">
        <DatePicker
          v-model="local.established_date"
          :label="$t('STOCK.CMR.ESTABLISHED_DATE')"
          without-hint
          clearable
          :disabled="disabled"
        ></DatePicker>
      </v-col>
      <v-col cols="12" md="3">
        <v-text-field
          v-model="local.vehicle_registration_number"
          :label="$t('STOCK.CMR.VEHICLE_REGISTRATION')"
          :disabled="disabled"
        ></v-text-field>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import DatePicker from "@/views/partials/form/DatePicker.vue";

const emptyParty = () => ({
  company_name: "",
  address: "",
  country: "",
  vat_number: "",
});

const emptyBoo = () => ({
  company_name: "",
  vat_number: "",
});

const MAX_GOODS_LINES = 4;

const emptyGoodsLine = () => ({
  _key: `cmr-goods-${Date.now()}-${Math.random()}`,
  name: "",
  hs_code: "",
  gross_weight: null,
  net_weight: null,
});

const emptyForm = () => ({
  sender: emptyParty(),
  boo_1: emptyBoo(),
  boo_2: emptyBoo(),
  consignee: emptyParty(),
  place_of_delivery: "",
  place_and_date_of_taking_over: "",
  goods_lines: [emptyGoodsLine()],
  established_in: "",
  established_date: null,
  vehicle_registration_number: "",
});

export default {
  name: "StockCmrForm",
  components: { DatePicker },
  props: {
    form: {
      type: Object,
      default: () => ({}),
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    saving: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      local: emptyForm(),
      maxGoodsLines: MAX_GOODS_LINES,
      hydrating: false,
      dirty: false,
      lastSignature: null,
      baseline: null,
    };
  },
  watch: {
    form: {
      immediate: true,
      handler() {
        this.hydrateFromProp();
      },
    },
    local: {
      deep: true,
      handler() {
        if (!this.hydrating) {
          this.dirty = true;
        }
      },
    },
  },
  methods: {
    cloneForm(value) {
      if (!value || typeof value !== "object") {
        return {};
      }
      return JSON.parse(JSON.stringify(value));
    },
    formSignature(value) {
      return JSON.stringify(value || {});
    },
    normalizedSignature(value) {
      return JSON.stringify(value, (key, item) => (item === "" ? null : item));
    },
    hydrateFromProp() {
      const signature = this.formSignature(this.form);
      if (signature === this.lastSignature) {
        return;
      }
      if (this.dirty && !this.saving) {
        return;
      }
      this.hydrate();
    },
    hydrate() {
      this.hydrating = true;
      const source = this.cloneForm(this.form);
      const defaults = emptyForm();
      this.local = {
        ...defaults,
        ...source,
        sender: { ...defaults.sender, ...(source.sender || {}) },
        boo_1: { ...defaults.boo_1, ...(source.boo_1 || {}) },
        boo_2: { ...defaults.boo_2, ...(source.boo_2 || {}) },
        consignee: { ...defaults.consignee, ...(source.consignee || {}) },
        goods_lines: (source.goods_lines || []).length
          ? source.goods_lines.slice(0, MAX_GOODS_LINES).map((line, index) => ({
              ...emptyGoodsLine(),
              ...line,
              _key: line._key || `cmr-goods-${index}`,
            }))
          : [emptyGoodsLine()],
      };
      this.lastSignature = this.formSignature(this.form);
      this.$nextTick(() => {
        this.hydrating = false;
        this.dirty = false;
        this.baseline = this.normalizedSignature(this.payload());
      });
    },
    addGoodsLine() {
      if (this.local.goods_lines.length >= MAX_GOODS_LINES) {
        return;
      }
      this.local.goods_lines.push(emptyGoodsLine());
    },
    removeGoodsLine(index) {
      this.local.goods_lines.splice(index, 1);
      if (!this.local.goods_lines.length) {
        this.local.goods_lines.push(emptyGoodsLine());
      }
    },
    payload() {
      const payload = JSON.parse(JSON.stringify(this.local));
      payload.goods_lines = (payload.goods_lines || [])
        .slice(0, MAX_GOODS_LINES)
        .map(({ _key, ...line }) => line);
      return payload;
    },
    isDirty() {
      if (!this.baseline) {
        return this.dirty;
      }
      return this.normalizedSignature(this.payload()) !== this.baseline;
    },
  },
};
</script>

<style scoped>
.cmr-party-card {
  height: 100%;
  border-color: #d7e3e0 !important;
  background: #fbfcfc;
}

.cmr-party-card__title {
  font-size: 0.95rem;
  font-weight: 600;
  color: #1d7f72;
  margin-bottom: 12px;
}
</style>
